import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Award, Download, CheckCircle } from 'lucide-react';

export default function PublicFormPage() {
  const { slug } = useParams();
  const [institution, setInstitution] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`/api/institutions/public/${slug}`)
      .then(res => {
        setInstitution(res.data);
        if (res.data.templates?.[0]?.id) setSelectedTemplate(res.data.templates[0].id);
      })
      .catch(() => setError('מוסד לא נמצא'))
      .finally(() => setLoading(false));
  }, [slug]);

  const currentTemplate = institution?.templates?.find(t => t.id === selectedTemplate);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post('/api/certificates', {
        institution_slug: slug,
        template_id: selectedTemplate,
        field_values: fieldValues,
        requester_name: name,
        requester_email: email
      });
      setResult(res.data);
    } catch {
      setError('שגיאה ביצירת האישור');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100vh'}}>
      <div>טוען...</div>
    </div>
  );

  if (error) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100vh'}}>
      <div className="card" style={{textAlign:'center', maxWidth:400}}>
        <p style={{color:'var(--danger)'}}>{error}</p>
      </div>
    </div>
  );

  if (result) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'var(--bg)', padding:20}}>
      <div className="card" style={{maxWidth:480, width:'100%', textAlign:'center'}}>
        <div style={{width:72, height:72, background:'#dcfce7', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px'}}>
          <CheckCircle size={36} color="var(--success)" />
        </div>
        <h2 style={{marginBottom:8}}>האישור מוכן!</h2>
        <p style={{color:'var(--text-muted)', marginBottom:24}}>לחץ להורדת המסמך</p>
        <a className="btn btn-primary btn-full" href={result.download_url} target="_blank" rel="noreferrer" style={{justifyContent:'center'}}>
          <Download size={18} /> הורדת האישור
        </a>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh', background:'var(--bg)', padding:'40px 20px'}}>
      <div style={{maxWidth:560, margin:'0 auto'}}>
        {/* כותרת */}
        <div style={{textAlign:'center', marginBottom:32}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:8}}>
            <Award size={28} color="var(--primary)" />
            <h1 style={{fontSize:22, fontWeight:700}}>{institution?.name}</h1>
          </div>
          <p style={{color:'var(--text-muted)'}}>בקשת אישור לימודים</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit}>
            {/* בחירת תבנית */}
            {institution?.templates?.length > 1 && (
              <div className="form-group" style={{marginBottom:16}}>
                <label className="label">סוג האישור</label>
                <select className="input" value={selectedTemplate} onChange={e => { setSelectedTemplate(e.target.value); setFieldValues({}); }}>
                  {institution.templates.map(t => t.id && <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}

            {/* פרטים אישיים */}
            <div className="form-group" style={{marginBottom:16}}>
              <label className="label">שם מלא *</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="ישראל ישראלי" required />
            </div>

            <div className="form-group" style={{marginBottom:16}}>
              <label className="label">כתובת מייל *</label>
              <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="israel@example.com" required />
            </div>

            {/* שדות התבנית */}
            {currentTemplate?.fields?.map(field => (
              <div key={field.id} className="form-group" style={{marginBottom:16}}>
                <label className="label">{field.label}{field.required ? ' *' : ''}</label>
                <input
                  className="input"
                  type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                  value={fieldValues[field.id] || ''}
                  onChange={e => setFieldValues({...fieldValues, [field.id]: e.target.value})}
                  required={field.required}
                />
              </div>
            ))}

            {error && <div style={{color:'var(--danger)', fontSize:13, marginBottom:12, textAlign:'center'}}>{error}</div>}

            <button className="btn btn-primary btn-full" type="submit" disabled={submitting}>
              <Award size={16} /> {submitting ? 'מכין אישור...' : 'בקשת אישור'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
